import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, TrendingUp, Target, Zap, ArrowRight, Shield, CreditCard, MessageCircle, Lock, Plus, ScanLine, Wrench, Briefcase, BarChart2, Filter } from "lucide-react";
import StickyCTABar from "@/components/StickyCTABar";
import CountdownTimer from "@/components/CountdownTimer";
import ExitIntentModal from "@/components/ExitIntentModal";
import { captureUTMParams } from "@/utils/utmUtils";
import "@/styles/landing.css";

export default function DigitalMarketingPage() {
  const navigate = useNavigate();
  const startAnalysis = () => navigate("/intake");
  const heroRef = useRef(null);
  const [offerExpired, setOfferExpired] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);

  // Capture UTM params on mount
  useEffect(() => { captureUTMParams(); }, []);

  const stats = [
    { value: "12,400+", label: "Resumes Analyzed" },
    { value: "3.2x", label: "More Callbacks" },
    { value: "94%", label: "ATS Pass Rate After Fix" },
    { value: "4 min", label: "Average Analysis Time" }
  ];

  const companyLogos = [
    { src: "/logos/dm_logo_1.png", alt: "Company 1" },
    { src: "/logos/dm_logo_2.png", alt: "Company 2" },
    { src: "/logos/dm_logo_3.png", alt: "Company 3" },
    { src: "/logos/dm_logo_4.png", alt: "Company 4" },
    { src: "/logos/dm_logo_5.png", alt: "Company 5" },
  ];

  const features = [
    {
      icon: <ScanLine className="w-6 h-6" />,
      title: "ATS Readiness Analyzer",
      description: "Your Canva-designed resume looks great to humans — but ATS can't parse it. We scan for format, keyword alignment, section structure, and parser compatibility, then give you a score out of 100.",
      example: 'Checks for "Google Analytics", "Meta Ads", "SEO" & 200+ DM keywords',
      badge: "Digital Marketer Essential"
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "ATS Blocker Fix Engine",
      description: "Don't just know what's wrong — fix it. Get exact, copy-paste-ready recommendations for every blocker. Rewrite bullets with the right keywords. Restructure sections the way ATS expects them.",
      example: 'Turns "managed social media" → "scaled organic reach by 180% via content strategy"',
      badge: null
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Career Velocity Simulator",
      description: "Should you double down on Performance Marketing or pivot to CRO? See how different career moves change your salary trajectory over 12, 24, and 36 months — with real Indian market data.",
      example: "Simulates: Agency SEO Exec → In-house SEO Lead (₹6L → ₹14L path)",
      badge: "Digital Marketer Essential"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Smart Job Matching",
      description: "Stop doom-scrolling Naukri. We match you to roles where your specific mix of skills — Google Ads + content + analytics — has the highest callback probability. Every listing gets a fit score.",
      example: '"92% match: Growth Marketing Manager at Razorpay — here\'s why"',
      badge: null
    },
    {
      icon: <BarChart2 className="w-6 h-6" />,
      title: "Market Signals & Trend Insights",
      description: "Know which digital marketing roles are surging (AI Marketing, Growth Hacking) and which are flattening — before you invest months in the wrong specialization.",
      example: '"Performance Marketing roles up 42% YoY in Bangalore"',
      badge: null
    },
    {
      icon: <Filter className="w-6 h-6" />,
      title: "Anti-Random Apply Strategy",
      description: "You're a marketer — you know spray-and-pray doesn't convert. PlacementOS builds you a targeted application funnel: prioritized roles, sequenced outreach, and a 30/60/90-day roadmap.",
      example: "Apply to 15 high-fit roles instead of 150 random ones",
      badge: "Digital Marketer Essential"
    }
  ];

  const testimonials = [
    {
      name: "Arjun Sharma",
      img: "/avatars/arjun_sharma.jpg",
      role: "SEO Specialist → Growth Marketing Lead, Bangalore",
      company: "Placed at Razorpay — ₹14 LPA",
      initials: "AS",
      rating: 5,
      text: "I'd applied to 200+ jobs in 3 months and got nothing. PlacementOS showed me my resume had 14 ATS blockers — including a header graphic that hid my name from parsers. Fixed everything in one evening. Got 4 interview calls that week."
    },
    {
      name: "Priya Menon",
      img: "/avatars/priya_menon.jpg",
      role: "Social Media Manager → Performance Marketing, Mumbai",
      company: "Moved from ₹5L → ₹16L in 6 months",
      initials: "PM",
      rating: 5,
      text: "The career velocity simulator was eye-opening. I was about to take another agency role, but PlacementOS showed me the in-house path would 3x my salary in 18 months. That data gave me the confidence to hold out for the right role."
    },
    {
      name: "Rohan Kulkarni",
      img: "/avatars/rohan_kulkarni.jpg",
      role: "Content Marketer → Brand Strategist, Pune",
      company: "3 offers in 2 weeks — accepted ₹11 LPA role",
      initials: "RK",
      rating: 5,
      text: "As a content marketer, my resume was full of portfolio links and creative descriptions — exactly what ATS filters out. PlacementOS restructured everything with the right keywords while keeping my voice. Best ₹99 I've ever spent."
    }
  ];

  const faqs = [
    {
      question: "What exactly do I get for ₹99?",
      answer: "You get a complete career analysis package: your ATS readiness score, a detailed list of every blocker in your resume with exact fix recommendations, a personalized 30/60/90-day career roadmap tailored to digital marketing roles, smart job matches with fit scores, and a career velocity simulation. The free analysis shows your ATS score and top blockers — you only pay if you want the full, detailed report."
    },
    {
      question: "Is this specifically built for digital marketers?",
      answer: "Yes. Our keyword database, role matching, and career roadmaps are tuned for digital marketing roles in India — including SEO, SEM, performance marketing, social media, content marketing, email marketing, and growth roles. We understand the difference between an agency trajectory and an in-house trajectory, and our recommendations reflect that."
    },
    {
      question: "How is this different from Naukri or LinkedIn?",
      answer: "Naukri and LinkedIn are job listing platforms — they show you jobs but don't tell you why you're not getting callbacks. PlacementOS diagnoses the root cause (usually ATS compatibility issues), fixes it, and then matches you to roles where your specific skill set has the highest chance of getting a response. Think of it as the strategy layer that sits on top of job boards."
    },
    {
      question: "Will my data be safe?",
      answer: "Absolutely. Your resume and analysis data are encrypted, never shared with third parties, and you can delete your data at any time. We use Razorpay for payment processing, which is PCI DSS Level 1 compliant — the same security standard used by major Indian banks."
    },
    {
      question: "I'm a fresher with less than 1 year of experience. Will this work?",
      answer: "Yes — in fact, freshers benefit the most because they typically have the most ATS issues (wrong formatting, missing keywords, weak bullet points). The career roadmap feature is especially valuable for freshers because it shows you exactly which skills to build and roles to target in your first 1-3 years."
    }
  ];

  return (
    <div className="ap-root min-h-screen">
      <ExitIntentModal lpSlug="digitalmarketing" />
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="ap-eyebrow ap-rise ap-rise-1 mb-6">Built for Digital Marketers in India</p>
              <h1 className="ap-display ap-rise ap-rise-2 font-extrabold tracking-tight text-[clamp(30px,5vw,52px)] leading-[1.06] text-[var(--ap-ink)] mb-6">
                Stop Getting Ghosted.<br />
                Start Getting <span className="ap-grad-text italic">Callbacks.</span>
              </h1>
              <p className="ap-rise ap-rise-3 text-xl text-[var(--ap-ink-2)] mb-8 leading-relaxed">
                Your SEO skills are sharp. Your ad campaigns convert. But your resume? It's getting auto-rejected before any recruiter sees it. PlacementOS fixes that in 4 minutes.
              </p>
              <div className="ap-rise ap-rise-4 mt-6">
                <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg" data-testid="dm-hero-cta">
                  Start My Free Analysis
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative ap-rise ap-rise-2">
              {/* floating 3D chips */}
              <div className="hidden md:flex ap-glass ap-float absolute -left-6 -top-5 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <TrendingUp className="w-5 h-5 text-[var(--ap-green)]" />
                <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">3.2x</div><div className="text-[11px] text-[var(--ap-muted)]">More Callbacks</div></div>
              </div>
              <div className="hidden md:flex ap-glass ap-float-2 absolute -right-6 -bottom-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>94</span>
                <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">94% pass rate</div><div className="text-[11px] text-[var(--ap-muted)]">ATS after fix</div></div>
              </div>

              <div className="ap-card ap-tilt overflow-hidden p-0 relative z-10 rounded-[26px]">
                <div className="px-4 py-3 flex items-center gap-2 border-b border-[var(--ap-line)] bg-[var(--ap-canvas)]">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <span className="text-xs text-[var(--ap-muted)] ml-2">placementos.ai/digital-marketing-analysis</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">31</span>
                    </div>
                    <div>
                      <h4 className="ap-display font-semibold text-[var(--ap-ink)]">ATS Readiness Score</h4>
                      <p className="text-sm text-red-500">High rejection risk — 4 critical blockers found</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Missing "Google Analytics", "Meta Ads", "SEO" in skills</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Header graphic hides your name from parsers</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-[var(--ap-ink-2)]">"Managed social media" — no growth metrics</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-[var(--ap-green)] mt-0.5">✓</span>
                      <span className="text-[var(--ap-ink-2)]">Fix available — estimated new score: 91</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[var(--ap-card)] border-b border-[var(--ap-line)] py-12">
        <div className="ap-section max-w-6xl grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div className="ap-display text-3xl md:text-4xl font-extrabold text-[var(--ap-ink)] mb-2">{stat.value}</div>
              <div className="text-sm text-[var(--ap-muted)]">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Companies Section */}
      <section className="bg-[var(--ap-canvas)] py-12">
        <div className="ap-section max-w-6xl text-center">
          <p className="text-[var(--ap-muted)] mb-8 font-medium">Our users have landed roles at</p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-14">
            {companyLogos.map((logo, idx) => (
              <div key={idx} className="h-16 md:h-20 flex items-center justify-center">
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-full w-auto object-contain max-w-[120px] md:max-w-[150px]"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The Real Problem</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            You're not unqualified.<br />
            You're <span className="italic">invisible.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed mb-12">
            You've run campaigns that drove lakhs in revenue. You've ranked pages on Google. You've scaled ad accounts from zero. But none of that matters if your resume never reaches a human being. Most digital marketing resumes get silently filtered out by ATS software — not because of skill gaps, but because of formatting mistakes and missing keywords you'd never think to check.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="ap-card p-8 text-left">
              <div className="ap-display text-5xl font-extrabold text-red-500 mb-2">88%</div>
              <p className="text-sm text-[var(--ap-ink-2)]">of resumes are rejected by ATS before a recruiter ever sees them</p>
            </div>
            <div className="ap-card p-8 text-left">
              <div className="ap-display text-5xl font-extrabold text-[var(--ap-orange)] mb-2">73%</div>
              <p className="text-sm text-[var(--ap-ink-2)]">of digital marketers apply to roles they're overqualified for — and still get ghosted</p>
            </div>
            <div className="ap-card p-8 text-left">
              <div className="ap-display text-5xl font-extrabold text-[var(--ap-violet)] mb-2">4.5 mo</div>
              <p className="text-sm text-[var(--ap-ink-2)]">average time a digital marketer in India spends job hunting without strategy</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The PlacementOS Way</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Fix first. Then apply<br />
              where you'll actually win.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)] max-w-3xl mx-auto">
              PlacementOS is an AI-powered career operating system that diagnoses why you're getting rejected, fixes your resume for ATS compatibility, and points you to the exact roles where your digital marketing skills have the highest callback potential.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-[var(--ap-violet)]" style={{ background: "var(--ap-violet-soft)" }}>1</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Upload Your Resume</h3>
              <p className="text-[var(--ap-muted)]">Drop your resume in any format. Our AI reads it the same way a recruiter's ATS would — and finds what they'd miss.</p>
            </div>
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-[var(--ap-violet)]" style={{ background: "var(--ap-violet-soft)" }}>2</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Get Your AI Diagnosis</h3>
              <p className="text-[var(--ap-muted)]">See your ATS score, exact blockers killing your applications, and a prioritized fix list — specific to digital marketing roles.</p>
            </div>
            <div className="ap-card ap-pop p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto text-[var(--ap-violet)]" style={{ background: "var(--ap-violet-soft)" }}>3</div>
              <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">Fix, Match & Apply Smart</h3>
              <p className="text-[var(--ap-muted)]">Apply the fixes, unlock job matches scored to your profile, and build a career roadmap that gets you from stuck to placed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Everything You Need</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Built for digital marketers<br />
              who are done guessing.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)] max-w-3xl mx-auto">
              Every feature is tuned for the way digital marketing careers actually work in India — from agency grind to in-house growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="ap-card ap-pop p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
                    <span className="text-[var(--ap-violet)]">{feature.icon}</span>
                  </div>
                  {feature.badge && (
                    <span className="ap-chip" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                      {feature.badge}
                    </span>
                  )}
                </div>
                <h3 className="ap-display text-2xl font-bold text-[var(--ap-ink)] mb-3">{feature.title}</h3>
                <p className="text-[var(--ap-ink-2)] mb-4 leading-relaxed">{feature.description}</p>
                <div className="bg-[var(--ap-canvas)] border-l-4 border-[var(--ap-violet)] p-4 rounded">
                  <p className="text-sm text-[var(--ap-muted)] italic">{feature.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Real Results</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Digital marketers who stopped<br />
              guessing and started winning.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">
              These are real career outcomes from real people in India's digital marketing industry.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative w-12 h-12 text-white rounded-full flex items-center justify-center font-bold overflow-hidden shrink-0" style={{ background: "var(--ap-fire)" }}>
                    {testimonial.initials}
                    {testimonial.img && <img src={testimonial.img} alt={testimonial.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-[var(--ap-ink)]">{testimonial.name}</div>
                    <div className="text-xs text-[var(--ap-muted)]">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[var(--ap-orange)]">★</span>
                  ))}
                </div>
                <p className="text-sm text-[var(--ap-ink-2)] mb-4 leading-relaxed">{testimonial.text}</p>
                <div className="text-sm font-semibold text-[var(--ap-violet)]">{testimonial.company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">The Transformation</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              From invisible to shortlisted.
            </h2>
            <p className="text-xl text-[var(--ap-ink-2)]">
              Here's what changes when you stop guessing and start using a system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="ap-card p-8 border-2" style={{ borderColor: "rgba(239,68,68,.35)" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-red-500">Before PlacementOS</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <span className="text-red-500 text-xl">×</span>
                  <span>Applied to 150+ jobs, got 0 callbacks</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <span className="text-red-500 text-xl">×</span>
                  <span>Resume looked "good" but ATS score was 23/100</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <span className="text-red-500 text-xl">×</span>
                  <span>No idea which roles actually matched your skills</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <span className="text-red-500 text-xl">×</span>
                  <span>Stuck at ₹4.5 LPA with no clear growth path</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <span className="text-red-500 text-xl">×</span>
                  <span>Refreshing Naukri & LinkedIn every 2 hours</span>
                </li>
              </ul>
            </div>

            <div className="ap-card p-8 border-2" style={{ borderColor: "rgba(22,163,74,.35)" }}>
              <h3 className="ap-display text-2xl font-bold mb-6 text-[var(--ap-green)]">After PlacementOS</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span>Applied to 18 matched roles, got 7 callbacks</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span>ATS score jumped from 23 → 91 after fixes</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span>Smart matching found roles you didn't know existed</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span>Clear roadmap: ₹4.5L → ₹12L in 8 months</span>
                </li>
                <li className="flex items-start gap-3 text-[var(--ap-ink-2)]">
                  <CheckCircle className="w-6 h-6 text-[var(--ap-green)] flex-shrink-0" />
                  <span>Placed as Growth Marketing Lead — remote</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Launch Pricing</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-4">
              Everything you need to get placed.
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">
              One plan. No upsells. No subscriptions. Pay once, own your career analysis forever.
            </p>
            <div className="mt-4">
              <CountdownTimer onExpired={() => setOfferExpired(true)} />
            </div>
          </div>

          <div className="ap-card p-8 md:p-12" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="text-center mb-8">
              <span className="ap-chip mb-4" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                {offerExpired ? "Price Updated" : "Launch Offer — 90% Off"}
              </span>
              <h3 className="ap-display text-3xl font-bold text-[var(--ap-ink)] mb-2">Complete Career Analysis</h3>
              <p className="text-[var(--ap-muted)]">Full ATS diagnosis + career roadmap + smart matching</p>
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">ATS Readiness Score & Full Analysis</span>
                <span className="text-[var(--ap-muted)] line-through">₹499</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">ATS Blocker Diagnosis + Fix Plan</span>
                <span className="text-[var(--ap-muted)] line-through">₹399</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Personalized Career Roadmap (30/60/90)</span>
                <span className="text-[var(--ap-muted)] line-through">₹599</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Smart Job Matching + Relevancy Scores</span>
                <span className="text-[var(--ap-muted)] line-through">₹299</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                <span className="text-[var(--ap-ink-2)]">Career Velocity Simulation</span>
                <span className="text-[var(--ap-muted)] line-through">₹99</span>
              </div>
              <div className="flex items-center justify-between py-3 font-semibold">
                <span className="text-[var(--ap-muted)]">Total Value</span>
                <span className="text-[var(--ap-muted)] line-through">₹1,995</span>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="text-[var(--ap-muted)] mb-2">You pay today</div>
              <div className="ap-display text-6xl font-extrabold text-[var(--ap-ink)]">
                {offerExpired ? (
                  <><span className="text-3xl">₹</span>499<p className="text-base text-[var(--ap-muted)] mt-2 font-normal">Offer expired</p></>
                ) : (
                  <><span className="text-3xl">₹</span>99<p className="text-[var(--ap-muted)] mt-2 text-base font-normal">One-time payment · Lifetime access</p></>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={startAnalysis}
                className="ap-btn ap-btn-fire ap-btn-lg w-full"
                data-testid="dm-pricing-cta"
              >
                Start My Free Analysis
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <p className="text-center text-sm text-[var(--ap-muted)]">
              Free analysis first — pay only if you want the full report
            </p>

            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)]">
              <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
              <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Got Questions?</p>
            <h2 className="ap-h2 ap-display text-[var(--ap-ink)]">Frequently asked questions.</h2>
          </div>

          <div className="mt-12 space-y-5">
            {faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="ap-card overflow-hidden transition-shadow"
                  style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                  <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-6 bg-transparent border-0 cursor-pointer">
                    <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.question}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                      style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                      <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">Ready?</p>
          <h2 className="ap-h2 ap-display text-[var(--ap-ink)] mb-6">
            Your next role is waiting.<br />
            Your resume just isn't <span className="ap-grad-text italic">reaching</span> it.
          </h2>
          <p className="text-xl text-[var(--ap-ink-2)] mb-8 leading-relaxed">
            In 4 minutes, you'll know exactly what's blocking your applications — and exactly how to fix it. The analysis is free. The transformation starts now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="ap-btn ap-btn-ghost ap-btn-lg">
              See All Features
            </a>
          </div>
          <div className="mt-6">
            <a href="/find-jobs" className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink-2)] underline underline-offset-2">
              Or find a free job →
            </a>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <StickyCTABar heroRef={heroRef} onCTAClick={startAnalysis} />
    </div>
  );
}
